import React, { useState, useEffect, useRef } from 'react';
import { Loader } from 'lucide-react';

/**
 * Optimized Image Component for Builder
 * Features:
 * - Lazy loading with Intersection Observer
 * - Skeleton placeholder
 * - Error handling with fallback
 * - Progressive loading
 */
const BuilderImage = ({
  src,
  alt,
  className = '',
  priority = false,
  fallbackSrc = '/images/placeholder-product.webp',
  onLoad,
  onError
}) => {
  const [imageSrc, setImageSrc] = useState(priority ? src : null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef(null);
  const observerRef = useRef(null);

  useEffect(() => {
    // If priority, load immediately
    if (priority) {
      setImageSrc(src);
      return;
    }

    // Lazy loading with Intersection Observer
    if (!imgRef.current) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !imageSrc) {
            setImageSrc(src);
            observerRef.current?.disconnect();
          }
        });
      },
      {
        rootMargin: '50px', // Start loading 50px before image enters viewport
        threshold: 0.01
      }
    );

    observerRef.current.observe(imgRef.current);

    return () => {
      observerRef.current?.disconnect();
    };
  }, [src, priority, imageSrc]);

  const handleLoad = (e) => {
    setIsLoading(false);
    setHasError(false);
    if (onLoad) onLoad(e);
  };

  const handleError = (e) => {
    console.warn(`Failed to load image: ${src}`);
    setHasError(true);
    setIsLoading(false);
    
    // Try fallback
    if (imageSrc !== fallbackSrc) {
      setImageSrc(fallbackSrc);
    }
    
    if (onError) onError(e);
  };

  return (
    <div ref={imgRef} className={`relative ${className}`}>
      {/* Loading Skeleton */}
      {isLoading && imageSrc && (
        <div className="absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-pulse rounded">
          <div className="flex items-center justify-center h-full">
            <Loader className="w-8 h-8 text-gray-400 animate-spin" />
          </div>
        </div>
      )}

      {/* Placeholder before lazy load */}
      {!imageSrc && (
        <div className="absolute inset-0 bg-gray-100 rounded">
          <div className="flex items-center justify-center h-full">
            <svg className="w-12 h-12 text-gray-300" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
            </svg>
          </div>
        </div>
      )}

      {/* Actual Image */}
      {imageSrc && (
        <img
          src={imageSrc}
          alt={alt}
          className={`w-full h-full object-cover transition-opacity duration-300 ${
            isLoading ? 'opacity-0' : 'opacity-100'
          }`}
          onLoad={handleLoad}
          onError={handleError}
          loading={priority ? 'eager' : 'lazy'}
        />
      )}

      {/* Error State */}
      {hasError && imageSrc === fallbackSrc && (
        <div className="absolute inset-0 bg-gray-50 flex items-center justify-center rounded">
          <div className="text-center">
            <svg className="w-12 h-12 text-gray-300 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <p className="text-xs text-gray-400">Immagine non disponibile</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default BuilderImage;
